/**
 * Definiciones de pasos para el sistema de tutorial interactivo.
 *
 * Cada tutorial tiene:
 *  - id: identificador unico (se usa para persistir en localStorage)
 *  - steps[]: pasos con target (selector CSS o null), titulo, descripcion, posicion, icon
 *
 * Tipos de icon: welcome, checklist, ready, action, info, default
 * Posiciones: right, bottom, left, top (solo aplica si hay target)
 */

// ─── Tutorial general de onboarding (6 pasos) ──────────────────────────────

export const GENERAL_TUTORIAL = {
  id: "onboarding_admin",
  steps: [
    {
      target: null,
      title: "Bienvenido a Habitia",
      description:
        "Esta guía te va a mostrar las partes principales de la plataforma.\n\nPuedes avanzar presionando el botón Siguiente o utilizando las flechas de tu teclado. Para salir puedes presionar el botón Omitir o Esc desde tu teclado.\nEn cada pantalla puedes presionar el botón (?) para consultar nuevamente el tutorial.",
      icon: "welcome",
    },
    {
      target: '[data-tutorial="sidebar"]',
      title: "Menu lateral",
      description:
        "Desde este menú puedes navegar en toda la plataforma. En el sector superior tienes los accesos fijos, Inicio y Mis Edificios, y en la parte inferior tienes cuatro secciones distintas que puedes expandir:\n\n- FINANZAS: Cobranza, Gastos, Estado de Cuenta, Informes, Cierre de Mes, Multas\n- OPERACIONES: Incidencias, Novedades, Proveedores, Obras, Revisiones, Documentos\n- RESIDENTES: Usuarios, Alta de Residente, Asambleas, Amenities\n- PERSONAL: Asistencia, Tareas, Turnos, Chat, Licencias, Liquidaciones\n\nLos Criterios de división de gastos y las Unidades se configuran desde Mis Edificios > Editar Edificio.\n\nLos números rojos (badges) te alertan de items pendientes en tiempo real.",
      position: "right",
    },
    {
      target: '[data-tutorial="header-building-selector"]',
      title: "Selector de edificio",
      description:
        "Si sos administrador de más de un edificio, desde aquí puedes elegir cuál ver. Podrás ver la información del edificio filtrado en toda la plataforma.\n\nMuchas de las funciones de la plataforma necesitan que filtres un edificio primero. Si ves opciones en gris en el menú lateral, es por esto. Por favor, escoge un edificio para poder observar correctamente la información.",
      position: "bottom",
    },
    {
      target: '[data-tutorial="header-user-menu"]',
      title: "Tu cuenta",
      description:
        "Haz clic en tu avatar para acceder a:\n\n- Mi Perfil: Desde aquí puedes editar nombre, documento, teléfono y tu foto.\n- Configuración: Desde aquí puedes elegir cómo recibir tus notificaciones (WhatsApp, Email o ambos)\n- Cerrar sesión",
      position: "bottom",
    },
    {
      target: null,
      title: "Primeros pasos recomendados",
      description:
        "Para empezar a usar Habitia sigue estos pasos:\n\n1. Dirígete a Mis Edificios y crea tu edificio.\n2. Dentro del edificio, carga las unidades utilizando las distintas opciones disponibles: manual, desde un Excel o desde IA utilizando una foto.\n3. Invita a los residentes desde Alta de Residente por WhatsApp o Email.\n4. Carga los gastos del mes en Finanzas > Gastos\n5. Configura los criterios de los gastos en Finanzas > Criterios\n6. Genera la liquidación mensual en Finanzas > Cierre de Mes",
      icon: "checklist",
    },
    {
      target: null,
      title: "Listo para empezar",
      description:
        "En cada pantalla, en la esquina inferior derecha, encontrarás el botón (?) con una guía específica de esa sección.\n\nTambién puedes chatear con Habi, tu asistente con inteligencia artificial, para hacer consultas sobre tus edificios.",
      icon: "ready",
    },
  ],
};

// ─── Tutoriales por pantalla ────────────────────────────────────────────────

export const SCREEN_TUTORIALS = {
  homeadmin: {
    id: "screen_homeadmin",
    title: "Dashboard",
    steps: [
      {
        target: '[data-tutorial="home-kpis"]',
        title: "Indicadores clave",
        description:
          "Estas tarjetas te muestran el estado financiero y operativo de tus edificios en tiempo real:\n\n- Recaudacion del Mes: cuanto se cobro y el % de cumplimiento\n- Morosidad (Unidades): cuantas unidades tienen deuda\n- Morosidad (Monto): cuanto se debe en total\n- Incidencias Pendientes: problemas sin resolver\n- Flujo de Caja: ingresos menos egresos y meses de reserva\n- Ratio Gastos Operativos: egresos sobre ingresos",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="home-acciones"]',
        title: "Acciones rapidas",
        description:
          "Estos botones te llevan directo a las acciones mas frecuentes:\n\n- Registrar gasto: abre el formulario de nuevo gasto\n- Enviar aviso: crea una novedad para los residentes\n- Ver morosos: va a Cobranza filtrado por deudas\n- Informe de gastos: genera el reporte del mes",
        position: "bottom",
        icon: "action",
      },
      {
        target: '[data-tutorial="home-semaforo"]',
        title: "Semaforo Operativo",
        description:
          "La tabla muestra el estado de cada edificio con colores:\n\n- Verde: todo normal\n- Amarillo: requiere atencion (morosidad moderada o incidencias)\n- Rojo: situacion critica\n\nLas columnas muestran: nombre del edificio, estado general, porcentaje de morosidad, alertas activas y si el cierre de mes esta completado.",
        position: "top",
        icon: "info",
      },
    ],
  },

  edificios: {
    id: "screen_edificios",
    title: "Mis Edificios",
    steps: [
      {
        target: '[data-tutorial="edificios-nuevo"]',
        title: "Boton Nuevo Edificio",
        description:
          "Desde este boton creas un edificio nuevo. En el siguiente paso se va a abrir el formulario para que veas cada campo.",
        position: "bottom",
        icon: "action",
      },
      {
        target: '[data-tutorial="edificios-form"]',
        title: "Formulario de creacion",
        description:
          "Este es el formulario para crear un edificio. Vamos a recorrer cada campo uno por uno.",
        position: "right",
        icon: "info",
        action: "edificios-abrir-modal",
      },
      {
        target: '[data-tutorial="edificios-campo-nombre"]',
        title: "Campo: Nombre *",
        description:
          "Ingresa el nombre del edificio tal como lo conocen los residentes.\n\nEjemplo: 'Torre Central', 'Residencial Obligado', 'Complejo Las Palmas'.\n\nEste campo es obligatorio.",
        position: "right",
        icon: "action",
      },
      {
        target: '[data-tutorial="edificios-campo-direccion"]',
        title: "Campo: Direccion *",
        description:
          "Empieza a escribir la direccion y se autocompleta con Google Maps. Al seleccionar una opcion del listado:\n\n- La direccion se formatea automaticamente\n- Se capturan las coordenadas (latitud/longitud)\n- Se completan Ciudad, Departamento y Pais\n\nSolo busca direcciones en Uruguay.",
        position: "right",
        icon: "action",
      },
      {
        target: '[data-tutorial="edificios-campos-auto"]',
        title: "Ciudad, Departamento y Pais",
        description:
          "Estos tres campos se completan automaticamente cuando seleccionas una direccion de Google Maps.\n\nSi necesitas, podes editarlos manualmente. Los tres son obligatorios.",
        position: "right",
        icon: "info",
      },
      {
        target: '[data-tutorial="edificios-campo-padron"]',
        title: "Campo: Padron",
        description:
          "Numero de padron catastral del edificio. Es el identificador que usa la Direccion Nacional de Catastro (DNC).\n\nEste campo es opcional.",
        position: "right",
        icon: "info",
      },
      {
        target: '[data-tutorial="edificios-campo-complejo"]',
        title: "Campo: Es complejo",
        description:
          "Activa esta casilla si el edificio es un complejo de edificios (multiples bloques en un mismo terreno).\n\nEsto afecta como se muestra en la plataforma (badge 'Complejo' vs 'Edificio').",
        position: "right",
        icon: "info",
      },
      {
        target: '[data-tutorial="edificios-btn-crear"]',
        title: "Confirmar creacion",
        description:
          "Cuando tengas todos los campos completos, hace clic en 'Crear' para guardar el edificio.\n\nSi falta algun campo obligatorio, se marca en rojo con el mensaje 'Requerido'.\n\nDespues de crear, el edificio aparece en tu lista y podes seleccionarlo en el header para empezar a configurarlo.",
        position: "top",
        icon: "action",
        cleanupAction: "edificios-cerrar-modal",
      },
      {
        target: '[data-tutorial="edificios-buscar"]',
        title: "Buscador",
        description:
          "Busca edificios por nombre, direccion o ciudad. El filtro se aplica en tiempo real mientras escribis.",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="edificios-toggle"]',
        title: "Vista Lista o Mapa",
        description:
          "Cambia entre dos vistas:\n\n- Lista: tarjetas con nombre, direccion, ciudad y un badge que indica si es 'Edificio' o 'Complejo'. Si tiene ubicacion, el boton verde 'Ver ubicacion' te lleva al mapa.\n\n- Mapa: mapa interactivo de Google Maps con un marcador por edificio. Hace clic en el marcador para ver el nombre y acceder a 'Ver detalles'.",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="edificios-lista"]',
        title: "Tarjetas de edificios",
        description:
          "Cada tarjeta muestra un edificio con su nombre, direccion y ciudad.\n\nHace clic en cualquier tarjeta para abrir 'Editar Edificio', donde configuras unidades, finanzas, personal, amenities y mas.\n\nSi un edificio tiene una solicitud de eliminacion pendiente, aparece semi-transparente con un badge amarillo.",
        position: "top",
        icon: "info",
      },
    ],
  },

  pagosedificio: {
    id: "screen_gastos",
    title: "Gastos",
    steps: [
      {
        target: '[data-tutorial="gastos-filtros"]',
        title: "Filtrar por periodo",
        description:
          "Usa los selectores de Ano y Mes para ver los gastos de un periodo especifico. Tambien podes alternar entre 'Activos' (gastos normales) y 'Eliminados' (papelera) con los botones de arriba.",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="gastos-nuevo"]',
        title: "Registrar un gasto",
        description:
          "Hace clic en 'Nuevo Gasto' para cargar una factura. Tenes dos opciones:\n\n- Carga manual: completas proveedor, descripcion, monto, IVA, moneda (Pesos o Dolares), criterio de division y categoria\n- Carga con IA: subis una foto o PDF de la factura y Habi extrae los datos automaticamente. Los campos se marcan con colores de confianza (verde = seguro, amarillo = revisar, rojo = verificar)\n\nSi el gasto es en dolares y el edificio tiene configurada la conversion automatica (en Editar Edificio > Finanzas), al pagar se convierte a pesos al tipo de cambio del BCU del dia anterior.",
        position: "bottom",
        icon: "action",
      },
      {
        target: '[data-tutorial="gastos-tabla"]',
        title: "Tabla de gastos",
        description:
          "Cada fila muestra un gasto con fecha, proveedor, descripcion, monto, categoria y estado. Hace clic en un gasto para ver el detalle completo, editarlo, marcarlo como pagado o eliminarlo.\n\nLos gastos eliminados van a la papelera y se pueden recuperar.",
        position: "top",
        icon: "info",
      },
    ],
  },

  cobranzaadmin: {
    id: "screen_cobranza",
    title: "Cobranza",
    steps: [
      {
        target: '[data-tutorial="cobranza-kpis"]',
        title: "Indicadores de cobranza",
        description:
          "Las tarjetas de arriba te muestran:\n\n- Unidades: total de unidades del edificio\n- Morosidad (Unidades): cuantas tienen deuda vencida\n- Deuda Vencida: monto total adeudado\n- Pend. Verificacion: pagos que subieron los residentes y necesitan tu aprobacion (requiere accion)",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="cobranza-aging"]',
        title: "Antiguedad de mora",
        description:
          "Este grafico muestra cuantas unidades tienen deuda segun el rango de dias:\n\n- 0-30 dias: deuda reciente\n- 31-60 dias: un mes de atraso\n- 61-90 dias: dos meses\n- 91-180 dias: trimestral\n- 180+ dias: deuda critica\n\nTe ayuda a priorizar a quien contactar primero.",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="cobranza-lista"]',
        title: "Verificar y cobrar",
        description:
          "Expandi cualquier unidad para ver sus pagos. Desde ahi podes:\n\n- 'Verificar': revisar el comprobante (foto de la transferencia) y aprobar o rechazar el pago\n- 'Registrar pago': cargar un cobro manual (efectivo u otro medio)\n- 'Ver comprobante': ver la imagen del comprobante en detalle\n\nUsa el buscador y las pestanas para filtrar por estado.",
        position: "top",
        icon: "action",
      },
    ],
  },

  cierredemes: {
    id: "screen_cierre",
    title: "Cierre de Mes",
    steps: [
      {
        target: '[data-tutorial="cierre-tabla"]',
        title: "Historial de cierres",
        description:
          "La tabla muestra todos los cierres generados con: Mes, Ano, Fecha de Creacion, Estado (OK o Error) y Publicacion (Pendiente o Aprobado).\n\nLas acciones por cierre son:\n- Ojo: revisar el detalle del cierre\n- PDF: descargar la liquidacion\n- Papelera: eliminar el cierre (revierte todos los cargos)",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="cierre-nuevo"]',
        title: "Generar un cierre",
        description:
          "Hace clic en 'Nuevo cierre' para generar la liquidacion mensual. Selecciona mes y ano.\n\nHabitia toma los gastos del periodo, los divide segun los criterios configurados, calcula intereses por mora (si hay deudas vencidas) y genera un pago por cada unidad.\n\nSi hay intereses, se muestra una vista previa para que decidas si incluirlos o descartarlos.",
        position: "left",
        icon: "action",
      },
      {
        target: null,
        title: "Antes de cerrar",
        description:
          "Asegurate de que:\n\n- Todos los gastos del mes esten cargados en Finanzas > Gastos\n- Los criterios de division esten configurados en Mis Edificios > Editar > Finanzas\n- Las unidades del edificio esten cargadas en Mis Edificios > Editar > Unidades\n\nSi necesitas corregir algo despues del cierre, podes eliminarlo y generar uno nuevo. Al eliminar se revierten todos los cargos y pagos generados.",
        icon: "info",
      },
    ],
  },

  estadocuentaedificio: {
    id: "screen_estadocuenta",
    title: "Estado de Cuenta",
    steps: [
      {
        target: null,
        title: "Movimientos del edificio",
        description:
          "Aca ves el flujo de caja completo: saldo anterior, gastos (-), cobranza (+) y saldo final. La banda de arriba te da el resumen visual.\n\nUsa las pestanas para cambiar entre fondos: Caja (operativo), Reserva, USD y fondos personalizados.",
        icon: "info",
      },
      {
        target: null,
        title: "Exportar",
        description:
          "Podes generar un PDF profesional con logo y detalle de movimientos (boton Imprimir), o descargar un archivo Excel con todos los datos (boton Descargar Excel). Ideal para enviar a la comision o para auditoria.",
        icon: "action",
      },
    ],
  },

  informegastos: {
    id: "screen_informegastos",
    title: "Informe de Gastos",
    steps: [
      {
        target: null,
        title: "Reporte del mes",
        description:
          "Este informe tiene cuatro secciones:\n\n1. Detalle de gastos agrupados por categoria (Consumos, Servicios, Mantenimiento, Administracion, Seguros, Impuestos, Otros)\n2. Resumen por fondo en pesos (Caja, Reserva, Obras)\n3. Resumen por fondo en dolares (si aplica)\n4. Tabla 'Gastos a pagar' por unidad: saldo anterior, pagos recibidos, gastos comunes, fondo de reserva, recargo por mora y total a pagar",
        icon: "info",
      },
    ],
  },

  criteriosporedificios: {
    id: "screen_criterios",
    title: "Criterios de Division",
    steps: [
      {
        target: null,
        title: "Como se reparten los gastos",
        description:
          "Los criterios definen como dividir cada gasto entre las unidades. Los mas comunes:\n\n- Todos (partes iguales): cada unidad paga lo mismo\n- Por superficie (m2): proporcional al tamano\n- Solo propietarios: inquilinos no pagan\n- Solo inquilinos: propietarios no pagan\n- Con garage: solo unidades con cochera\n\nHace clic en 'Agregar Criterio' para sumar uno nuevo a tu edificio. Despues, al cargar un gasto en la seccion Gastos, vas a poder elegir con que criterio se divide.",
        icon: "info",
      },
    ],
  },

  multasadmin: {
    id: "screen_multas",
    title: "Multas",
    steps: [
      {
        target: null,
        title: "Gestion de multas",
        description:
          "Crea multas a residentes que incumplan el reglamento (Art. 10, Ley 10.751). Hace clic en 'Nueva Multa' y completa: unidad, tipo de usuario (propietario/inquilino), moneda (UYU, USD, UI o UR), monto y motivo.\n\nSi la multa esta en UI o UR, al incluirla en el cierre de mes se convierte automaticamente a pesos al valor del BCU del dia anterior.\n\nArriba se muestran los valores de referencia actuales del BCU (UI, UR, USD).\n\nEstados de una multa:\n- Activa: vigente, se incluye automaticamente en el proximo cierre de mes\n- Pagada: el residente la abono\n- Anulada: se cancelo (requiere ingresar un motivo)\n\nUsa los filtros y la busqueda para encontrar multas especificas.",
        icon: "info",
      },
    ],
  },

  incidenciasedificio: {
    id: "screen_incidencias",
    title: "Incidencias",
    steps: [
      {
        target: null,
        title: "Reportes de problemas",
        description:
          "Aca gestionas los problemas que reportan los residentes. Cada incidencia tiene un estado (Reportada, En Proceso, Pendiente, Resuelta) y una prioridad.\n\nTiempos maximos de resolucion segun prioridad:\n- Alta: 48 horas\n- Media: 72 horas\n- Baja: 7 dias\n\nPodes responder al residente (con opcion de WhatsApp), cambiar estado y prioridad, y agregar notas internas que solo ven los administradores.",
        icon: "info",
      },
    ],
  },

  novedadesedificio: {
    id: "screen_novedades",
    title: "Novedades",
    steps: [
      {
        target: null,
        title: "Comunicados",
        description:
          "Publica anuncios para los residentes: cortes de servicio, mantenimiento, eventos, etc.\n\nEscribi el titulo y la descripcion, y opcionalmente usa 'Mejorar con IA' para que Habi reescriba el texto con tono profesional.\n\nPodes guardar como borrador (no visible para residentes) o publicar directamente. El historial muestra todas las novedades ordenadas por fecha.",
        icon: "info",
      },
    ],
  },

  proveedoresedificio: {
    id: "screen_proveedores",
    title: "Proveedores",
    steps: [
      {
        target: null,
        title: "Directorio de proveedores",
        description:
          "Registra los proveedores y contratistas del edificio con sus datos: nombre, tipo (Servicios Publicos, Mantenimiento, Limpieza, etc.), RUT, razon social, contacto y direccion.\n\nPodes verlos en lista (con busqueda por nombre, RUT o tipo) o en un mapa de Google Maps con la ubicacion de cada uno.\n\nSi intentas crear un proveedor con un RUT que ya existe, Habitia te avisa para evitar duplicados.",
        icon: "info",
      },
    ],
  },

  proyectosadmin: {
    id: "screen_proyectos",
    title: "Obras y Proyectos",
    steps: [
      {
        target: null,
        title: "Gestion de obras",
        description:
          "Registra proyectos de construccion o mejora con: nombre, descripcion, contratista, presupuesto, fechas y estado (Planificado, En Curso, Pausado, Finalizado, Cancelado).\n\nCada proyecto tiene un legajo documental con checklist de documentos requeridos por organismo:\n- BPS: certificado al dia, planilla de trabajo\n- DGI: certificado unico, constancia RUT\n- Intendencia: permiso de obra, planos\n- Seguros: poliza RC, todo riesgo\n- Contratos: contrato firmado, garantia",
        icon: "info",
      },
    ],
  },

  revisionesadmin: {
    id: "screen_revisiones",
    title: "Revisiones",
    steps: [
      {
        target: null,
        title: "Inspecciones tecnicas",
        description:
          "Registra inspecciones del edificio (ascensores, instalaciones, tanques, etc.) con titulo, empresa que la realiza, fecha y estado (Programada o Completada).\n\nLleva un historial para documentar el mantenimiento preventivo y tenerlo disponible si un organismo lo solicita.",
        icon: "info",
      },
    ],
  },

  documentacionadmin: {
    id: "screen_documentacion",
    title: "Documentos",
    steps: [
      {
        target: null,
        title: "Archivo digital",
        description:
          "Subi documentos importantes del edificio: reglamentos, actas de asamblea, polizas, contratos, facturas archivadas.\n\nCada documento tiene: descripcion, fecha, tipo (Factura, Reglamento, Acta, Otro) y el archivo adjunto. Funciona como un archivo digital centralizado que podes consultar en cualquier momento.",
        icon: "info",
      },
    ],
  },

  residentes: {
    id: "screen_unidades",
    title: "Unidades",
    steps: [
      {
        target: '[data-tutorial="unidades-grilla"]',
        title: "Grilla de unidades",
        description:
          "Cada tarjeta muestra una unidad del edificio con su numero, piso, dormitorios, banos, m2, propietario e inquilino. Si una unidad no tiene propietario asignado, aparece una advertencia en rojo.\n\nSi administras varios edificios sin filtrar, se agrupan por edificio.\n\nHace clic en cualquier tarjeta para abrir el detalle, donde podes editar datos, ver pagos de la unidad y sus incidencias.",
        position: "top",
        icon: "info",
      },
    ],
  },

  gestionusuarios: {
    id: "screen_usuarios",
    title: "Usuarios",
    steps: [
      {
        target: null,
        title: "Gestion de residentes",
        description:
          "Tiene dos pestanas: 'Residentes activos' (ya activaron cuenta) y 'Pendientes de invitacion'.\n\nPara crear un residente: hace clic en 'Nuevo Residente', completa edificio, unidad, rol (Propietario/Inquilino), nombre, apellido, tipo de documento (CI, DNI, Pasaporte, RUT), numero, email y celular.\n\nSi un propietario vende o un inquilino se muda, usa 'Reemplazar' para transferir la vinculacion sin perder historial.",
        icon: "info",
      },
    ],
  },

  onboardingresidente: {
    id: "screen_onboarding",
    title: "Alta de Residente",
    steps: [
      {
        target: '[data-tutorial="onboarding-pendientes"]',
        title: "Pendientes de invitacion",
        description:
          "Arriba ves los residentes que todavia no activaron su cuenta. Cada uno muestra nombre, celular, email, unidad y rol (Propietario/Inquilino).\n\nPodes editar sus datos directamente, y usar el boton 'Enviar' o 'Re-enviar' para mandarles la invitacion de nuevo.",
        position: "bottom",
        icon: "info",
      },
      {
        target: '[data-tutorial="onboarding-form"]',
        title: "Invitar un residente",
        description:
          "1. Selecciona el edificio\n2. Selecciona la unidad\n3. Elegí el rol: Propietario o Inquilino\n4. Completa nombre, celular (para WhatsApp) y email (opcional)\n5. Hace clic en 'Enviar por WhatsApp' o 'Enviar por Email'\n\nSe genera un link de activacion valido por 48 horas. Tambien podes copiarlo con el boton 'Copiar' y enviarlo por otro medio.",
        position: "top",
        icon: "action",
      },
    ],
  },

  asambleas: {
    id: "screen_asambleas",
    title: "Asambleas",
    steps: [
      {
        target: null,
        title: "Asambleas de copropietarios",
        description:
          "Crea la asamblea con fecha, hora, modalidad (presencial, remota o mixta), lugar y orden del dia. Al convocar, la citacion les llega SOLO a los propietarios vigentes por WhatsApp o email; a quien no tiene domicilio electronico le imprimis la citacion en papel con constancia.\n\nEn el detalle ves quien recibio la citacion y quien confirmo asistencia. El dia anterior el sistema manda el recordatorio solo. El acta desde el audio sigue en Herramientas > Acta desde audio.",
        icon: "info",
      },
    ],
  },

  reservasamenities: {
    id: "screen_amenities",
    title: "Amenities",
    steps: [
      {
        target: null,
        title: "Reservas de espacios comunes",
        description:
          "Gestiona las reservas de amenities (piscina, salon de eventos, estacionamiento, etc.) desde un calendario mensual.\n\nHace clic en un dia para ver las reservas o crear una nueva. Podes cancelar reservas existentes o bloquear horarios para mantenimiento.\n\nLos amenities se configuran desde Editar Edificio > pestana Amenities, donde defines nombre, costo por turno y moneda (UYU, USD, UI o UR). Si el costo esta en UI o UR, se convierte a pesos al valor del BCU del dia de uso al momento del cierre de mes.",
        icon: "info",
      },
    ],
  },

  editaredificio: {
    id: "screen_editaredificio",
    title: "Editar Edificio",
    steps: [
      {
        target: '[data-tutorial="editar-tabs"]',
        title: "Pestanas de configuracion",
        description:
          "El edificio se configura en estas pestanas:\n\nDatos | Unidades | Personal | Amenities | Finanzas | Saldos Proveedores | Comision | Comunicaciones\n\nVamos a recorrer las principales. El tutorial va a cambiar de tab automaticamente para mostrarte el contenido.\n\nQuien administra el edificio lo asigna un superadmin (pestana Administradores, que solo ve el superadmin).\n\nArriba a la derecha tenes el boton 'Eliminar edificio' que envia una solicitud al superadmin.",
        position: "bottom",
        icon: "info",
        action: "editar-ir-tab-datos",
      },
      {
        target: '[data-tutorial="editar-tab-datos"]',
        title: "Tab Datos — Informacion del Edificio",
        description:
          "Campos de esta seccion:\n\n- Nombre *: nombre del edificio\n- Direccion *: ubicacion completa\n- Ciudad *: se autocompleta desde la direccion\n- Departamento *: se autocompleta\n- Pais *: Uruguay por defecto\n- Padron: numero de padron catastral (opcional)\n\nHace clic en 'Guardar' para confirmar los cambios. El boton solo se activa si modificaste algo.",
        position: "right",
        icon: "info",
        action: "editar-ir-tab-datos",
      },
      {
        target: '[data-tutorial="editar-tab-unidades"]',
        title: "Tab Unidades — Apartamentos",
        description:
          "Arriba ves el contador de unidades registradas y los botones de accion:\n\n- 'Agregar Unidad': carga manual de un apartamento\n- 'Cargar Excel': subi un archivo .xlsx (podes descargar el template)\n- 'Cargar con IA': pega texto (de WhatsApp, email) o subi fotos/PDFs y la IA extrae los datos\n- 'Calcular Alicuotas': calcula la milesima de cada unidad segun valor catastral o m2\n\nCada tarjeta de unidad muestra: numero, piso, dormitorios, banos, m2, valor catastral, alicuota, balcon, garage, propietario e inquilino.\n\nHace clic en el lapiz para editar o la papelera para eliminar.",
        position: "right",
        icon: "action",
        action: "editar-ir-tab-unidades",
      },
      {
        target: null,
        title: "Unidades — Carga masiva y Alicuotas",
        description:
          "Carga desde Excel:\n1. Descarga el template con 'Descargar Template Excel'\n2. Completa las columnas (numero, piso, m2, dormitorios, banos, prop_nombre, prop_cedula, inq_nombre, inq_cedula)\n3. Subi el archivo y revisa la vista previa\n4. Confirma con 'Guardar X unidades'\n\nCarga con IA:\n1. Pega texto o subi imagenes/PDFs\n2. Hace clic en 'Procesar con IA'\n3. Revisa las unidades detectadas\n4. Confirma con 'Guardar X unidades'\n\nCalculo de Alicuotas:\n- 'Calcular desde valor catastral': usa los valores catastrales\n- 'Estimar desde m2': usa la superficie\n- La suma debe dar 1000 milesimas exactas\n- Podes editar cada alicuota manualmente",
        icon: "info",
      },
      {
        target: '[data-tutorial="editar-tab-personal"]',
        title: "Tab Personal — Empleados",
        description:
          "Para agregar un empleado, completa el formulario 'Agregar Personal':\n\n- Tipo de documento *: CI, DNI, Pasaporte, RUT u Otro\n- Nombre * y Apellidos *\n- Documento *: numero segun tipo\n- Email * y Celular *\n- Horario *: ej. '08:00 - 17:00'\n- Dias *: ej. 'Lunes a Viernes'\n- Sueldo *: monto mensual\n- Inicio Actividad *: fecha de ingreso\n\nAl agregar, se crea automaticamente un usuario en la plataforma vinculado al edificio. Los empleados aparecen como tarjetas debajo del formulario.",
        position: "right",
        icon: "action",
        action: "editar-ir-tab-empleado",
      },
      {
        target: '[data-tutorial="editar-tab-amenities"]',
        title: "Tab Amenities — Espacios comunes",
        description:
          "Arriba se muestran los valores de referencia del BCU (UI, UR, USD) para que veas las cotizaciones actuales.\n\nHace clic en 'Agregar Amenity' para crear un espacio. Campos:\n\n- Nombre *: ej. Piscina, Salon de Eventos, Gimnasio\n- Categoria *: Con costo o Sin costo\n- Moneda: UYU, USD, UI (Unidad Indexada) o UR (Unidad Reajustable)\n- Exclusivo o Compartido: si solo una persona/unidad puede reservar por turno\n- Capacidad maxima: cuantas reservas simultaneas (si es compartido)\n\nTurnos: defini los horarios disponibles con nombre (ej. 'Mediodia', 'Noche'), hora de inicio, hora de fin y costo por turno.\n\nSi el costo esta en UI o UR, se convierte automaticamente a pesos al valor del BCU del dia de uso, al momento del cierre de mes.\n\nPodes editar o eliminar amenities existentes. Las reservas se gestionan desde Residentes > Amenities.",
        position: "right",
        icon: "info",
        action: "editar-ir-tab-amenities",
      },
      {
        target: '[data-tutorial="editar-tab-finanzas"]',
        title: "Tab Finanzas — Configuracion financiera",
        description:
          "Esta seccion tiene varias cards:\n\n1. Distribucion de Gastos: metodo para repartir (Partes iguales, Por superficie, Por alicuotas)\n\n2. Mora y Vencimientos:\n   - Tipo de mora: Porcentaje anual (max 12%, Ley 19.604) o Monto fijo\n   - Dias de gracia: 0-90 dias antes de aplicar interes\n   - Dia de vencimiento: dia del mes (1-28)\n\n3. Fondo de Reserva: Sin fondo, Porcentaje del gasto comun, o Monto fijo\n\n4. Fondos Personalizados: crea fondos extra (Calefaccion, Obra, etc.)\n\n5. Multas Automaticas: activa/desactiva con dias de atraso, monto y moneda (UYU, USD, UI o UR)\n\n6. Cuenta Bancaria y Saldos: cuentas en UYU y USD + saldos iniciales\n\n7. Tipo de Cambio y Conversion: cotizaciones automaticas del BCU (USD, UI, UR). Configura si los gastos en dolares se convierten automaticamente a pesos o se mantienen en USD\n\n8. Criterios de Division de Gastos: agrega los criterios que se usan al dividir gastos entre unidades",
        position: "right",
        icon: "info",
        action: "editar-ir-tab-finanzas",
      },
      {
        target: null,
        title: "Finanzas — Fondos personalizados",
        description:
          "Los fondos personalizados te permiten crear ahorros especificos. Hace clic en '+ Agregar Fondo':\n\n- Nombre del fondo *: ej. 'Calefaccion', 'Obra Fachada'\n- Metodo de cobro *: Porcentaje del gasto comun o Monto fijo mensual\n- Porcentaje o Monto segun metodo\n- Saldo inicial UYU y USD\n\nLa tabla muestra cada fondo con su estado (Activo/Inactivo). Podes editar, activar o desactivar fondos desde los links de acciones.",
        icon: "info",
      },
      {
        target: '[data-tutorial="editar-tab-comision"]',
        title: "Tab Comision — Directiva del edificio",
        description:
          "Asigna los integrantes de la comision directiva del edificio. Ningun campo es obligatorio.\n\nRoles disponibles:\n- Presidente\n- Secretario\n- Tesorero\n- Primer Vocal\n- Segundo Vocal\n\nCada rol se asigna con un buscador de usuarios (solo aparecen los residentes vinculados al edificio). Selecciona '— Sin asignar —' para dejar vacio.\n\nHace clic en 'Guardar Comision' para confirmar. Si no hay residentes vinculados, aparece un aviso pidiendo que agregues residentes primero.",
        position: "right",
        icon: "info",
        action: "editar-ir-tab-comision",
      },
    ],
  },

  asistenciaempleados: {
    id: "screen_asistencia",
    title: "Asistencia",
    steps: [
      {
        target: null,
        title: "Registro de asistencia",
        description:
          "Consulta la asistencia diaria de los empleados con hora de entrada, salida y horas trabajadas.\n\nFiltra por empleado especifico y rango de fechas. Los indicadores de arriba muestran cuantos estan presentes hoy y el promedio de horas.\n\nLos empleados registran su entrada/salida desde la app Habitia Empleados.",
        icon: "info",
      },
    ],
  },

  tareasadmin: {
    id: "screen_tareas",
    title: "Tareas",
    steps: [
      {
        target: null,
        title: "Asignar trabajos",
        description:
          "Crea tareas para empleados con: titulo, descripcion, empleado asignado, fecha limite y prioridad (Baja, Media, Alta).\n\nEstados: Pendiente, En progreso, Completada, Cancelada.\n\nFiltra por empleado, estado o prioridad para ver rapidamente que esta pendiente.",
        icon: "info",
      },
    ],
  },

  turnosadmin: {
    id: "screen_turnos",
    title: "Turnos",
    steps: [
      {
        target: null,
        title: "Calendario de horarios",
        description:
          "Gestiona los turnos en un calendario mensual. Crea turnos por empleado, dia y horario.\n\nModo lote: asigna el mismo horario para un rango de dias (ej: lunes a viernes 8-16hs).\n\nTambien podes registrar licencias como turno (reglamentaria, estudio, maternidad/paternidad, enfermedad, sin goce, etc.).",
        icon: "info",
      },
    ],
  },

  chatadmin: {
    id: "screen_chat",
    title: "Chat con Empleados",
    steps: [
      {
        target: null,
        title: "Mensajeria interna",
        description:
          "Chat en tiempo real con los empleados. La lista de la izquierda muestra las conversaciones ordenadas por ultimo mensaje. A la derecha se ve el historial completo.\n\nPodes enviar mensajes de texto y de audio (grabar desde la app). El badge rojo en el sidebar te avisa cuantos mensajes no leidos tenes.",
        icon: "info",
      },
    ],
  },

  solicitudesadmin: {
    id: "screen_licencias",
    title: "Licencias",
    steps: [
      {
        target: null,
        title: "Solicitudes de empleados",
        description:
          "Recibis solicitudes de licencia que podes aprobar o rechazar (con motivo obligatorio al rechazar).\n\nPestanas: Todas, Pendientes, Aprobadas, Rechazadas.\n\nTipos de licencia: Reglamentaria, Extraordinaria, Por Estudio, Maternidad/Paternidad, Sin Goce, Por Enfermedad, Otro. Cada tipo tiene un color identificatorio.",
        icon: "info",
      },
    ],
  },

  recibosadmin: {
    id: "screen_liquidaciones",
    title: "Liquidaciones",
    steps: [
      {
        target: null,
        title: "Recibos de sueldo",
        description:
          "Selecciona mes y ano para generar las liquidaciones de los empleados. Habitia cruza automaticamente:\n\n- Registro de asistencia (horas reales)\n- Turnos programados\n- Licencias aprobadas (no descuentan)\n\nEl resultado es: sueldo base + adicionales - descuentos = neto a pagar. Podes expandir cada liquidacion para ver el desglose y descargar o imprimir el recibo.",
        icon: "info",
      },
    ],
  },
};

// Helper para buscar tutorial por screen ID
export const getScreenTutorial = (screenId) => {
  return SCREEN_TUTORIALS[screenId] || null;
};
